import React from 'react'
import { Tooltip } from './Tooltip'

const VARIANTS = {
  primary: {
    base: 'bg-primary hover:bg-primary-600',
    text: 'text-white',
    icon: 'text-white/90',
  },
  secondary: {
    base: 'bg-white ring-1 ring-zinc-300 hover:bg-gray-50',
    text: 'text-gray-700',
    icon: 'text-zinc-400',
  },
  ghost: {
    base: 'bg-transparent hover:bg-gray-50',
    text: 'text-gray-600 hover:text-blue-600',
    icon: 'text-gray-400 group-hover:text-blue-600',
  },
  danger: {
    base: 'bg-red-600 hover:bg-red-700',
    text: 'text-white',
    icon: 'text-white/90',
  },
  action: {
    base: 'bg-transparent hover:bg-transparent p-0',
    text: 'text-gray-600 hover:text-blue-500',
    icon: 'text-gray-400 group-hover:text-blue-500',
  },
}

const FONT_WEIGHTS = {
  normal: 'font-normal',
  medium: 'font-medium',
  semibold: 'font-semibold',
}

const SIZES = {
  sm: {
    button: 'px-3 py-1.5 text-xs rounded-lg',
    icon: 'h-4 w-4',
  },
  md: {
    button: 'px-4 py-2 text-sm rounded-lg',
    icon: 'h-5 w-5',
  },
  icon: {
    button: 'rounded-full',
    icon: 'h-5 w-5',
  },
}

export function Button({
  children,
  variant = 'primary',
  size = 'md',
  fontWeight = 'semibold',
  type = 'button',
  icon: Icon,
  iconPosition = 'left', // "left" | "right"
  iconClassName = '', // optional override
  className = '',
  tooltip, // ✅ new: string (optional)
  tooltipSide = 'top',
  tooltipAlign = 'center',
  tooltipDelayMs = 0,
  ...props
}) {
  const v = VARIANTS[variant] ?? VARIANTS.primary
  const s = SIZES[size] ?? SIZES.md
  const fw = FONT_WEIGHTS[fontWeight] ?? FONT_WEIGHTS.semibold

  const hasText = React.Children.count(children) > 0
  const iconOnly = Boolean(Icon) && !hasText

  const iconClasses = [s.icon, iconClassName || v.icon].filter(Boolean).join(' ')

  const buttonEl = (
    <button
      type={type}
      className={[
        'group inline-flex items-center transition-colors',
        iconOnly ? '' : 'gap-2',
        fw,
        v.base,
        v.text,
        s.button,
        className,
      ].join(' ')}
      // ✅ for icon-only accessibility if user provides tooltip but forgets aria-label
      aria-label={props['aria-label'] ?? (iconOnly && tooltip ? tooltip : undefined)}
      {...props}
    >
      {Icon && iconPosition === 'left' && <Icon className={iconClasses} aria-hidden="true" />}

      {hasText ? <span>{children}</span> : null}

      {Icon && iconPosition === 'right' && <Icon className={iconClasses} aria-hidden="true" />}
    </button>
  )

  if (!tooltip) return buttonEl

  return (
    <Tooltip content={tooltip} side={tooltipSide}>
      {buttonEl}
    </Tooltip>
  )
}
